import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', red: '#E24B4A', blue: '#1A4C7C' };

const inputCls = 'w-full px-2.5 py-1.5 border rounded-sm text-xs bg-white';

function ListHeader({ title, hint, onAdd, addTestId }) {
  return (
    <div className="flex items-center justify-between mt-3 mb-1">
      <div>
        <p className="text-xs font-semibold" style={{ color: C.navy }}>{title}</p>
        {hint && <p className="text-[10px]" style={{ color: C.slate }}>{hint}</p>}
      </div>
      <button type="button" onClick={onAdd} data-testid={addTestId}
        className="flex items-center gap-1 text-[11px] font-medium" style={{ color: C.blue }}>
        <Plus className="w-3 h-3" />Add
      </button>
    </div>
  );
}

export default function StructuredDeclineForm({ api, claim, onDone, onCancel }) {
  const [mode, setMode] = useState('structured');
  const [provisions, setProvisions] = useState([{ clause_reference: '', clause_text: '', application: '' }]);
  const [evidence, setEvidence] = useState([{ document: '', author: '', date: '', finding: '' }]);
  const [reasoning, setReasoning] = useState('');
  const [wouldChange, setWouldChange] = useState('');
  const [partials, setPartials] = useState([]);
  const [contactName, setContactName] = useState(claim.claimant_name || '');
  const [quickCategory, setQuickCategory] = useState('withdrawn');
  const [quickReason, setQuickReason] = useState('');
  const [saving, setSaving] = useState(false);

  const upd = (setter) => (i, k, v) => setter(list => list.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const updProv = upd(setProvisions), updEv = upd(setEvidence), updPar = upd(setPartials);
  const rm = (setter) => (i) => setter(list => list.filter((_, idx) => idx !== i));

  const provOk = provisions.some(p => p.clause_reference.trim() && p.clause_text.trim() && p.application.trim());
  const evOk = evidence.some(e => e.document.trim() && e.finding.trim());
  const reasonOk = reasoning.trim().length > 0;
  const ready = provOk && evOk && reasonOk;

  const submit = async () => {
    setSaving(true);
    try {
      if (mode === 'quick') {
        await api.post(`/claims/${claim.claim_id}/decline`, { reason: quickReason, category: quickCategory });
        toast.success(`Claim declined — ${quickCategory}`);
      } else {
        await api.post(`/claims/${claim.claim_id}/decline-structured`, {
          provisions, evidence,
          reasoning: reasoning.split('\n').map(s => s.trim()).filter(Boolean),
          what_would_change: wouldChange.split('\n').map(s => s.trim()).filter(Boolean),
          partial_elements: partials,
          contact_name: contactName,
        });
        toast.success('Claim declined — DOC-35 decline letter issued to the claim record');
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Decline refused');
    }
    setSaving(false);
  };

  return (
    <div data-testid="structured-decline-form">
      <div className="flex items-center gap-3 mb-2">
        <p className="text-xs font-semibold" style={{ color: C.navy }}>Decline Claim</p>
        <label className="flex items-center gap-1 text-[11px] cursor-pointer" style={{ color: C.slate }}>
          <input type="radio" checked={mode === 'structured'} onChange={() => setMode('structured')} data-testid="decline-mode-structured" />
          Substantive (clause + evidence)
        </label>
        <label className="flex items-center gap-1 text-[11px] cursor-pointer" style={{ color: C.slate }}>
          <input type="radio" checked={mode === 'quick'} onChange={() => setMode('quick')} data-testid="decline-mode-quick" />
          Withdrawn / duplicate only
        </label>
      </div>

      {mode === 'quick' ? (
        <div className="space-y-2">
          <select value={quickCategory} onChange={e => setQuickCategory(e.target.value)} data-testid="decline-quick-category"
            className={inputCls} style={{ maxWidth: 220 }}>
            <option value="withdrawn">Withdrawn by the insured</option>
            <option value="duplicate">Duplicate lodgement</option>
          </select>
          <Input value={quickReason} onChange={e => setQuickReason(e.target.value)} placeholder="Short note (mandatory)"
            className="text-xs rounded-sm" data-testid="decline-quick-reason" />
        </div>
      ) : (
        <>
          <p className="text-[10px] px-2 py-1.5 rounded-sm mb-1" style={{ background: 'rgba(226,75,74,0.06)', color: '#B03230' }}>
            The onus of proving an exclusion sits with us. This decline cannot be recorded without the provision quoted in full, the evidence relied on, and the reasoning — all of it prints on the DOC-35 letter with review and AFCA rights.
          </p>
          <ListHeader title="1 · Provisions relied on" hint="Clause reference, the clause text in full, and how it applies here"
            onAdd={() => setProvisions(p => [...p, { clause_reference: '', clause_text: '', application: '' }])} addTestId="decline-add-provision" />
          {provisions.map((p, i) => (
            <div key={i} className="p-2 rounded-sm mb-1.5 space-y-1" style={{ background: 'rgba(26,76,124,0.04)' }} data-testid={`decline-provision-${i}`}>
              <div className="flex gap-1.5">
                <input value={p.clause_reference} onChange={e => updProv(i, 'clause_reference', e.target.value)}
                  placeholder="Clause reference — e.g. Part D, Exclusion 7 (Wear and Tear)" className={inputCls} data-testid={`decline-provision-ref-${i}`} />
                {provisions.length > 1 && <button onClick={rm(setProvisions).bind(null, i)} type="button"><Trash2 className="w-3.5 h-3.5" style={{ color: C.slate }} /></button>}
              </div>
              <textarea value={p.clause_text} onChange={e => updProv(i, 'clause_text', e.target.value)} rows={2}
                placeholder="The clause text, quoted in full from the wording" className={inputCls} data-testid={`decline-provision-text-${i}`} />
              <textarea value={p.application} onChange={e => updProv(i, 'application', e.target.value)} rows={2}
                placeholder="How it applies here — specific to this loss, not a category" className={inputCls} data-testid={`decline-provision-application-${i}`} />
            </div>
          ))}

          <ListHeader title="2 · Evidence relied on" hint="Each report must address the exclusion cited — the insured may request copies"
            onAdd={() => setEvidence(e => [...e, { document: '', author: '', date: '', finding: '' }])} addTestId="decline-add-evidence" />
          {evidence.map((e, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 mb-1.5" data-testid={`decline-evidence-${i}`}>
              <input value={e.document} onChange={ev => updEv(i, 'document', ev.target.value)} placeholder="Document / report" className={`${inputCls} col-span-4`} data-testid={`decline-evidence-doc-${i}`} />
              <input value={e.author} onChange={ev => updEv(i, 'author', ev.target.value)} placeholder="Author" className={`${inputCls} col-span-2`} />
              <input type="date" value={e.date} onChange={ev => updEv(i, 'date', ev.target.value)} className={`${inputCls} col-span-2`} />
              <input value={e.finding} onChange={ev => updEv(i, 'finding', ev.target.value)} placeholder="What it establishes" className={`${inputCls} col-span-3`} data-testid={`decline-evidence-finding-${i}`} />
              {evidence.length > 1 && <button onClick={rm(setEvidence).bind(null, i)} type="button" className="col-span-1"><Trash2 className="w-3.5 h-3.5" style={{ color: C.slate }} /></button>}
            </div>
          ))}

          <p className="text-xs font-semibold mt-3 mb-1" style={{ color: C.navy }}>3 · Reasoning <span className="font-normal text-[10px]" style={{ color: C.slate }}>(one step per line — connects the evidence to the provision)</span></p>
          <textarea value={reasoning} onChange={e => setReasoning(e.target.value)} rows={3}
            placeholder={'The assessor found ...\nThat brings the loss within Exclusion 7 because ...'} className={inputCls} data-testid="decline-reasoning" />

          <ListHeader title="Partial payment (optional)" hint="If part of the claim is accepted, list each element"
            onAdd={() => setPartials(p => [...p, { element: '', decision: '', amount: '' }])} addTestId="decline-add-partial" />
          {partials.map((p, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 mb-1.5">
              <input value={p.element} onChange={e => updPar(i, 'element', e.target.value)} placeholder="Element" className={`${inputCls} col-span-5`} />
              <select value={p.decision} onChange={e => updPar(i, 'decision', e.target.value)} className={`${inputCls} col-span-3`}>
                <option value="">Decision…</option><option value="Accepted">Accepted</option><option value="Declined">Declined</option>
              </select>
              <input type="number" value={p.amount} onChange={e => updPar(i, 'amount', parseFloat(e.target.value) || '')} placeholder="Amount $" className={`${inputCls} col-span-3`} />
              <button onClick={rm(setPartials).bind(null, i)} type="button" className="col-span-1"><Trash2 className="w-3.5 h-3.5" style={{ color: C.slate }} /></button>
            </div>
          ))}

          <p className="text-xs font-semibold mt-3 mb-1" style={{ color: C.navy }}>What would change this decision <span className="font-normal text-[10px]" style={{ color: C.slate }}>(optional, one per line)</span></p>
          <textarea value={wouldChange} onChange={e => setWouldChange(e.target.value)} rows={2}
            placeholder="A service record showing the unit was maintained ..." className={inputCls} data-testid="decline-would-change" />

          <div className="mt-2" style={{ maxWidth: 280 }}>
            <Input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Letter addressee (contact name)"
              className="text-xs rounded-sm" data-testid="decline-contact-name" />
          </div>
        </>
      )}

      <div className="flex gap-2 mt-3">
        <Button size="sm" disabled={saving || (mode === 'quick' ? !quickReason.trim() : !ready)} onClick={submit}
          className="text-xs rounded-md" style={{ background: C.red }} data-testid="decline-submit-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (mode === 'quick' ? 'Record quick decline' : 'Decline — issue DOC-35 letter')}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel} className="text-xs rounded-md">Cancel</Button>
      </div>
      {mode === 'structured' && !ready && (
        <p className="text-[10px] mt-1.5" style={{ color: C.red }} data-testid="decline-requirements-hint">
          Required before this decline can record: {[!provOk && 'a provision with reference, full text and application', !evOk && 'an evidence item with a finding', !reasonOk && 'the reasoning'].filter(Boolean).join(' · ')}
        </p>
      )}
    </div>
  );
}
